import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from '@/backend/lib/firebase';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export interface UploadImageParams {
  file: File;
  storagePath: string;
  onProgress?: (progress: number) => void;
}

export interface UploadImageResult {
  url: string;
  path: string;
  size: number;
}

/**
 * Validate an image file before upload.
 * Returns an error message string, or null if valid.
 */
export const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return 'Only JPEG, PNG, and WebP images are accepted';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'Image must be smaller than 5 MB';
  }
  return null;
};

/**
 * Compress an image client-side using browser-image-compression (dynamically imported).
 * Reduces file size and dimensions to keep storage and bandwidth lean.
 */
export const compressImage = async (file: File): Promise<File> => {
  const imageCompression = (await import('browser-image-compression')).default;
  return imageCompression(file, {
    maxSizeMB: 2,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  });
};

/**
 * Upload an image to Firebase Storage with optional progress tracking.
 * The file is compressed before upload.
 */
export const uploadImage = async (
  params: UploadImageParams
): Promise<UploadImageResult> => {
  const { file, storagePath, onProgress } = params;

  const validationError = validateImageFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  // Compress before uploading
  const compressed = await compressImage(file);

  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').substring(0, 100);
  const fullPath = `${storagePath}/${timestamp}_${safeName}`;
  const storageRef = ref(storage, fullPath);

  // Explicitly set content type so Firebase Storage rules can validate it
  const metadata = { contentType: compressed.type || file.type };
  const uploadTask = uploadBytesResumable(storageRef, compressed, metadata);

  return new Promise<UploadImageResult>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot: UploadTaskSnapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(Math.round(progress));
      },
      (error) => {
        switch (error.code) {
          case 'storage/unauthorized':
            reject(new Error('You do not have permission to upload files'));
            break;
          case 'storage/canceled':
            reject(new Error('Upload was cancelled'));
            break;
          default:
            reject(new Error('Upload failed. Please try again.'));
        }
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({
            url: downloadURL,
            path: fullPath,
            size: compressed.size,
          });
        } catch {
          reject(new Error('Failed to get download URL. Please try again.'));
        }
      }
    );
  });
};

/**
 * Delete an image from Firebase Storage by its download URL or storage path.
 */
export const deleteImage = async (urlOrPath: string): Promise<void> => {
  try {
    const fileRef = ref(storage, urlOrPath);
    await deleteObject(fileRef);
  } catch (error: unknown) {
    if (error instanceof Error && error.message.includes('storage/object-not-found')) {
      return; // Already deleted, no-op
    }
    throw new Error('Failed to delete image. Please try again.');
  }
};
